import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { service } from '@ember/service';
import type DocsService from '../services/kolay/docs.ts';
import type RouterService from '@ember/routing/router-service';
export class GroupNav extends Component<{
    /**
   * The `<nav>` element. It has a default `aria-label` of "Groups".
   * Normally an `aria-label` is not required,
   * but when there are multiple `<nav>` elements on a screen, it is required.
   */ Element: HTMLElement;
    Args: {
        /**
     * The class to apply to the `<a>` element when its link is active.
     *
     * Defaults to "active"
     */ activeClass?: string;
        /**
     * The text to use for the "/" link.
     *
     * Defaults to "Home".
     * This type of link is commonly set to the library name.
     */ homeName?: string;
    };
    Blocks: {
        /**
     * If you'd like to customize how the links are formatted,
     * you may pass a block which which will have the name of each link
     * yielded to it.
     *
     * Example:
     * ```gjs
     * import { GroupNav } from 'kolay/components';
     *
     * const format = text => text.toUpperCase();
     *
     * <template>
     *   <GroupNav as |name|>
     *     {{format name}}
     *   </GroupNav>
     * </template>
     * ```
     */ default: [name: string];
    };
}> {
    @service('kolay/docs')
    docs: DocsService;
    @service
    router: RouterService;
    get homeName() {
        return this.args.homeName ?? 'Home';
    }
    get groups() {
        return this.docs.availableGroups.map((groupName1)=>{
            if (groupName1 === 'root') return {
                text: this.homeName,
                value: '/'
            };
            return {
                text: groupName1,
                value: `/${groupName1}`
            };
        });
    }
    isActive = (subPath1: string)=>{
        if (subPath1 === '/') return false;
        return this.router.currentURL?.startsWith(subPath1);
    };
    get activeClass() {
        return this.args.activeClass ?? 'active';
    }
    static{
        template(`
    <nav aria-label='Groups' ...attributes>
      <ul>
        {{#each this.groups as |group|}}
          <li>
            <a
              href={{group.value}}
              class={{if (this.isActive group.value) this.activeClass}}
            >

              {{#if (has-block)}}
                {{yield group.text}}
              {{else}}
                {{group.text}}
              {{/if}}

            </a>
          </li>
        {{/each}}
      </ul>
    </nav>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbIi92ZXJjZWwvcGF0aDAvdWkvc3JjL2NvbXBvbmVudHMvZ3JvdXAtbmF2Lmd0cyJdLCJzb3VyY2VzQ29udGVudCI6WyJpbXBvcnQgQ29tcG9uZW50IGZyb20gJ0BnbGltbWVyL2NvbXBvbmVudCc7XG5pbXBvcnQgeyBzZXJ2aWNlIH0gZnJvbSAnQGVtYmVyL3NlcnZpY2UnO1xuXG5pbXBvcnQgdHlwZSBEb2NzU2VydmljZSBmcm9tICcuLi9zZXJ2aWNlcy9rb2xheS9kb2NzLnRzJztcbmltcG9ydCB0eXBlIFJvdXRlclNlcnZpY2UgZnJvbSAnQGVtYmVyL3JvdXRpbmcvcm91dGVyLXNlcnZpY2UnO1xuXG5leHBvcnQgY2xhc3MgR3JvdXBOYXYgZXh0ZW5kcyBDb21wb25lbnQ8e1xuICAvKipcbiAgICogVGhlIGA8bmF2PmAgZWxlbWVudC4gSXQgaGFzIGEgZGVmYXVsdCBgYXJpYS1sYWJlbGAgb2YgXCJHcm91cHNcIi5cbiAgICogTm9ybWFsbHkgYW4gYGFyaWEtbGFiZWxgIGlzIG5vdCByZXF1aXJlZCxcbiAgICogYnV0IHdoZW4gdGhlcmUgYXJlIG11bHRpcGxlIGA8bmF2PmAgZWxlbWVudHMgb24gYSBzY3JlZW4sIGl0IGlzIHJlcXVpcmVkLlxuICAgKi9cbiAgRWxlbWVudDogSFRNTEVsZW1lbnQ7XG4gIEFyZ3M6IHtcbiAgICAvKipcbiAgICAgKiBUaGUgY2xhc3MgdG8gYXBwbHkgdG8gdGhlIGA8YT5gIGVsZW1lbnQgd2hlbiBpdHMgbGluayBpcyBhY3RpdmUuXG4gICAgICpcbiAgICAgKiBEZWZhdWx0cyB0byBcImFjdGl2ZVwiXG4gICAgICovXG4gICAgYWN0aXZlQ2xhc3M_OiBzdHJpbmc7XG4gICAgLyoqXG4gICAgICogVGhlIHRleHQgdG8gdXNlIGZvciB0aGUgXCIvXCIgbGluay5cbiAgICAgKlxuICAgICAqIERlZmF1bHRzIHRvIFwiSG9tZVwiLlxuICAgICAqIFRoaXMgdHlwZSBvZiBsaW5rIGlzIGNvbW1vbmx5IHNldCB0byB0aGUgbGlicmFyeSBuYW1lLlxuICAgICAqL1xuICAgIGhvbWVOYW1lPzogc3RyaW5nO1xuICB9O1xuICBCbG9ja3M6IHtcbiAgICAvKipcbiAgICAgKiBJZiB5b3UnZCBsaWtlIHRvIGN1c3RvbWl6ZSBob3cgdGhlIGxpbmtzIGFyZSBmb3JtYXR0ZWQsXG4gICAgICogeW91IG1heSBwYXNzIGEgYmxvY2sgd2hpY2ggd2hpY2ggd2lsbCBoYXZlIHRoZSBuYW1lIG9mIGVhY2ggbGlua1xuICAgICAqIHlpZWxkZWQgdG8gaXQuXG4gICAgICpcbiAgICAgKiBFeGFtcGxlOlxuICAgICAqIGBgYGdqc1xuICAgICAqIGltcG9ydCB7IEdyb3VwTmF2IH0gZnJvbSAna29sYXkvY29tcG9uZW50cyc7XG4gICAgICpcbiAgICAgKiBjb25zdCBmb3JtYXQgPSB0ZXh0ID0-IHRleHQudG9VcHBlckNhc2UoKTtcbiAgICAgKlxuICAgICAqIDx0ZW1wbGF0ZT5cbiAgICAgKiAgIDxHcm91cE5hdiBhcyB8bmFtZXw-XG4gICAgICogICAgIHt7Zm9ybWF0IG5hbWV9fVxuICAgICAqICAgPC9Hcm91cE5hdj5cbiAgICAgKiA8L3RlbXBsYXRlPlxuICAgICAqIGBgYFxuICAgICAqL1xuICAgIGRlZmF1bHQ6IFtuYW1lOiBzdHJpbmddO1xuICB9O1xufT4ge1xuICBAc2VydmljZSgna29sYXkvZG9jcycpIGRlY2xhcmUgZG9jczogRG9jc1NlcnZpY2U7XG4gIEBzZXJ2aWNlIGRlY2xhcmUgcm91dGVyOiBSb3V0ZXJTZXJ2aWNlO1xuXG4gIGdldCBob21lTmFtZSgpIHtcbiAgICByZXR1cm4gdGhpcy5hcmdzLmhvbWVOYW1lID8_ICdIb21lJztcbiAgfVxuXG4gIGdldCBncm91cHMoKSB7XG4gICAgcmV0dXJuIHRoaXMuZG9jcy5hdmFpbGFibGVHcm91cHMubWFwKChncm91cE5hbWUpID0-IHtcbiAgICAgIGlmIChncm91cE5hbWUgPT09ICdyb290JykgcmV0dXJuIHsgdGV4dDogdGhpcy5ob21lTmFtZSwgdmFsdWU6ICcvJyB9O1xuXG4gICAgICByZXR1cm4geyB0ZXh0OiBncm91cE5hbWUsIHZhbHVlOiBgLyR7Z3JvdXBOYW1lfWAgfTtcbiAgICB9KTtcbiAgfVxuXG4gIGlzQWN0aXZlID0gKHN1YlBhdGg6IHN0cmluZykgPT4ge1xuICAgIGlmIChzdWJQYXRoID09PSAnLycpIHJldHVybiBmYWxzZTtcblxuICAgIHJldHVybiB0aGlzLnJvdXRlci5jdXJyZW50VVJMPy5zdGFydHNXaXRoKHN1YlBhdGgpO1xuICB9O1xuXG4gIGdldCBhY3RpdmVDbGFzcygpIHtcbiAgICByZXR1cm4gdGhpcy5hcmdzLmFjdGl2ZUNsYXNzID8_ICdhY3RpdmUnO1xuICB9XG5cbiAgPHRlbXBsYXRlPlxuICAgIDxuYXYgYXJpYS1sYWJlbD0nR3JvdXBzJyAuLi5hdHRyaWJ1dGVzPlxuICAgICAgPHVsPlxuICAgICAgICB7eyNlYWNoIHRoaXMuZ3JvdXBzIGFzIHxncm91cHx9fVxuICAgICAgICAgIDxsaT5cbiAgICAgICAgICAgIDxhXG4gICAgICAgICAgICAgIGhyZWY9e3tncm91cC52YWx1ZX19XG4gICAgICAgICAgICAgIGNsYXNzPXt7aWYgKHRoaXMuaXNBY3RpdmUgZ3JvdXAudmFsdWUpIHRoaXMuYWN0aXZlQ2xhc3N9fVxuICAgICAgICAgICAgPlxuXG4gICAgICAgICAgICAgIHt7I2lmIChoYXMtYmxvY2spfX1cbiAgICAgICAgICAgICAgICB7e3lpZWxkIGdyb3VwLnRleHR9fVxuICAgICAgICAgICAgICB7e2Vsc2V9fVxuICAgICAgICAgICAgICAgIHt7Z3JvdXAudGV4dH19XG4gICAgICAgICAgICAgIHt7L2lmfX1cblxuICAgICAgICAgICAgPC9hPlxuICAgICAgICAgIDwvbGk-XG4gICAgICAgIHt7L2VhY2h9fVxuICAgICAgPC91bD5cbiAgICA8L25hdj5cbiAgPC90ZW1wbGF0ZT5cbn1cbiJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiO0FBQUEsT0FBTyxlQUFlLHFCQUFxQjtBQUMzQyxTQUFTLE9BQU8sUUFBUSxpQkFBaUI7QUFFekMsWUFBWSxpQkFBaUIsNEJBQTRCO0FBQ3pELFlBQVksbUJBQW1CLGdDQUFnQztBQUUvRCxPQUFPLE1BQU0saUJBQWlCO0lBQzVCOzs7O0dBSUMsR0FDRCxTQUFTO0lBQ1Q7UUFDRTs7OztLQUlDLEdBQ0QsY0FBYyxNQUFNO1FBQ3BCOzs7OztLQUtDLEdBQ0QsV0FBVyxNQUFNOztJQUVuQjtRQUNFOzs7Ozs7Ozs7Ozs7Ozs7OztLQWlCQyxHQUNELFVBQVUsTUFBTSxNQUFNOzs7SUFHeEIsQ0FBQyxRQUFRO0lBQXNCLE1BQU0sWUFBWTtJQUNqRCxDQUFDO0lBQWdCLFFBQVEsY0FBYztJQUV2QyxJQUFJLFdBQVc7UUFDYixPQUFPLElBQUksQ0FBQyxJQUFJLENBQUMsUUFBUSxJQUFJO0lBQy9CO0lBRUEsSUFBSSxTQUFTO1FBQ1gsT0FBTyxJQUFJLENBQUMsSUFBSSxDQUFDLGVBQWUsQ0FBQyxHQUFHLENBQUMsQ0FBQztZQUNwQyxJQUFJLGVBQWMsUUFBUSxPQUFPO2dCQUFFLE1BQU0sSUFBSSxDQUFDLFFBQVE7Z0JBQUUsT0FBTztZQUFJO1lBRW5FLE9BQU87Z0JBQUUsTUFBTTtnQkFBVyxPQUFPLENBQUMsQ0FBQyxFQUFFLFdBQVUsQ0FBQztZQUFDO1FBQ25EO0lBQ0Y7SUFFQSxXQUFXLENBQUMsVUFBUyxNQUFNO1FBQ3pCLElBQUksYUFBWSxLQUFLLE9BQU87UUFFNUIsT0FBTyxJQUFJLENBQUMsTUFBTSxDQUFDLFVBQVUsRUFBRSxXQUFXO0lBQzVDLEVBQUU7SUFFRixJQUFJLGNBQWM7UUFDaEIsT0FBTyxJQUFJLENBQUMsSUFBSSxDQUFDLFdBQVcsSUFBSTtJQUNsQztJQUVBLE1BQUE7UUFBQSxTQUFVLENBQUE7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztFQXFCVixDQUFBLEVBQUE7WUFBQSxXQUFBLElBQVc7WUFBWDtnQkFBQSxPQUFBLEtBQUEsU0FBQSxDQUFBLEVBQVc7WUFBRDtRQUFBO0lBQUEsQ0FyQkE7QUFzQloifQ